/**
 * Description: Initialise the live search for the header Site Search
 * 
 * @class SiteSearch
 * @typedef {SiteSearch}
 */

class SiteSearch {
    constructor(domNode) {
        this.domNode = domNode;
        this.inputNode = domNode.querySelector('input[type="search"]');
        this.resultContainer = this.domNode.querySelector('.search-results');
        this.innerContainer = '';
        this.previousSearchString = '';
        this.timeout = 0;
        this.jsonURL = '';

        this.inputNode.addEventListener(
            'keyup',
            this.onInputKeyup.bind(this)
        );

        window.addEventListener(
            'load',
            this.init.bind(this),
            true
        );
    }

    /**
     * Creates and builds any required initial markup for the live search to work
     */
    init() {
        this.jsonURL = this.domNode.getAttribute('data-fetch-url');
        const innerContainer = document.createElement('div');
        innerContainer.classList.add('inner-container');
        const innerContainerElm = this.resultContainer.appendChild(innerContainer);
        this.innerContainer = innerContainerElm;
    }


    /**
     * Checks if the passed string is different when compared with the previously stored search string this.previousSearchString
     * @param  {String}  value  The search query string that we want to check that has changed
     */
    hasChanged(value){
        if(value == this.previousSearchString){
            return false;
        }

        return true;
    }


    /**
     * Handles all keyup keyboard events
     * @param  {String}  string  The string that is to be escaped
     */
    escape(string) {
        return string
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");
    }


    /**
     * Builds output messages
     * @param  {String}  value  The value for the message content
     * @param  {String}  customClass  Custom class to be added to the message element
     */
    buildMessage(value, customClass){
        const fragment = new DocumentFragment();
        const messageElm = document.createElement('div');
        messageElm.classList.add('sa-message');
        if(customClass){
            messageElm.classList.add(customClass);
        }
        const message = document.createElement('span');
        message.textContent = value;
        messageElm.append(message);
        fragment.append(messageElm);
        return fragment;
    }


    /**
     * Builds Search Result
     * @param  {String}  title  The title content for the search result
     * @param  {String}  href  The link url link for the search result
     */
    buildSearchResult(title, href){
        const listItem = document.createElement('li');
        listItem.classList.add('result');
        const link = document.createElement('a');
        link.href = href;
        link.target = '_blank';
        const para = document.createElement('p');
        para.textContent = title;
        const icon = document.createElement('i');
        icon.classList.add('sa-icon--arrow-right');
        link.append(para);
        link.append(icon);
        listItem.append(link);
        return listItem;
    }

    
    /**
     * Handles all keyup keyboard events
     * @param  {Object}  event  The event that triggered this method
     */
    onInputKeyup(event) {
        this.innerContainer.classList.add('is-active');

        if(!this.hasChanged(this.inputNode.value)){
            return;
        }

        this.previousSearchString = this.inputNode.value;

        if(this.inputNode.textLength == 0){
            this.innerContainer.innerHTML = '';
            this.innerContainer.classList.remove('is-active');
            return;
        }

        if(this.inputNode.textLength <= 3){
            this.innerContainer.innerHTML = '';
            this.innerContainer.append(
                this.buildMessage('Please Keep Typing...', 'sa-message--info')
            );
            return;
        }

        clearTimeout(this.timeout);
        this.innerContainer.innerHTML = '';
        this.innerContainer.append(
            this.buildMessage('Searching...', 'sa-message--info')
        );
        this.timeout = setTimeout(
            this.fetchSearchResults.bind(this),
            700
        );
    }


    /**
     * Fetches the results from JSON query
     */
    async fetchSearchResults(){
        const cleanValue = this.escape(this.inputNode.value);
        const searchURL = this.jsonURL + cleanValue;
        const innerContainer = this.innerContainer;

        let responseJSON;

        try {
            const response = await fetch(searchURL);

            if (!response.ok) {
                this.innerContainer.innerHTML = '';
                innerContainer.append(
                    this.buildMessage('Whoops something went wrong, please try again.', 'sa-message--error')
                );
                throw new Error(`${response.status} ${response.statusText}`);
            }

            responseJSON = await response.json();
            
        } catch (error) {
            this.innerContainer.innerHTML = '';
            innerContainer.append(
                this.buildMessage('Whoops something went wrong, please try again.', 'sa-message--error')
            );

            if (error instanceof SyntaxError) {
                console.warn('There was a SyntaxError when fetching the JSON', error);
            } else {
                console.warn('There was an error', error);
            }
        }

        if (responseJSON) {
            const resultsArr = responseJSON.list;
            innerContainer.innerHTML = '';

            if(!resultsArr.length){
                innerContainer.append(
                    this.buildMessage('No Results Found.', 'sa-message--info')
                );
                return;
            }
            
            const title = `<span>Best Matches</span>`;
            const list = document.createElement('ul');

            for (let i = 0; i < resultsArr.length; i++) {
                if(i <= 2){
                    const result = resultsArr[i];
                    list.appendChild( this.buildSearchResult(result.title, result.url) );
                }
            }

            innerContainer.insertAdjacentHTML('afterbegin', title);
            innerContainer.append(list);

            const cta = `
            <a href="/search/?q=` + cleanValue + `" class="button button--primary">
                <span>View All Search Results (` + resultsArr.length + `)</span>
                <i class="sa-icon--search"></i>
            </a>
            `;

            innerContainer.insertAdjacentHTML(
                'beforeend',
                cta
            );
        }
    }
}

exports.SiteSearch = SiteSearch;